import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
  Output,
  EventEmitter,
} from "@angular/core";
import {
  EditorState,
  StateEffect,
  Extension,
} from "@codemirror/state";
import {
  EditorView,
  ViewUpdate,
  Decoration,
} from "@codemirror/view";
import { sql } from "@codemirror/lang-sql";
import { tags } from "@lezer/highlight";
import { Diagnostic } from "@codemirror/lint";

import {
  keymap,
  highlightSpecialChars,
  drawSelection,
  highlightActiveLine,
  dropCursor,
  rectangularSelection,
  crosshairCursor,
  lineNumbers,
  highlightActiveLineGutter,
} from "@codemirror/view";
import {
  HighlightStyle,
  defaultHighlightStyle,
  syntaxHighlighting,
  indentOnInput,
  bracketMatching,
  foldKeymap,
} from "@codemirror/language";
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from "@codemirror/commands";
import { searchKeymap, highlightSelectionMatches } from "@codemirror/search";
import {
  autocompletion,
  completionKeymap,
  closeBrackets,
  closeBracketsKeymap,
  CompletionContext,
} from "@codemirror/autocomplete";
import { lintKeymap, setDiagnostics } from "@codemirror/lint";
import { CodeEditorService } from "@services/code-editor.service";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-code-editor",
  templateUrl: "./code-editor.component.html",
  styleUrls: ["./code-editor.component.scss"],
})
export class CodeEditorComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild("editor") editor: any;
  @Input() readOnly;
  @Input() control;
  @Input() decorations = [];
  @Input() diagnostics: {
    content: string;
    message: string;
    severity: "error" | "warning";
  }[] = [];
  @Input() autocomplete: { type: string; label: string }[] = [];

  @Input() processedContent: {
    text: string;
    from: number;
    to: number;
  }; //processed content -> to be used to indicate previously used content if highlighted and used
  @Output() highlightedText = new EventEmitter<{
    text: string;
    from: number;
    to: number;
  }>(); //emits currently highlighted content along with its lines
  public currentlyHighlightedText = "";
  public editorView;
  public destroy$ = new Subject();
  public theme = EditorView.theme(
    (window as any).isDarkTheme ? {}:
    {
    "&.cm-editor.cm-focused": {
      outline: "none",

    },
    ".cm-gutters": {
      backgroundColor: "var(--layout-background-color)",
   
      border: "none",
    },
    ".cm-activeLineGutter": {
      // backgroundColor: "#cceeff44",
    },
    ".cm-gutterElement": {
      color: "#888",
      fontFamily: "monospace",
      padding: "0px",
    },
    ".cm-error": {
      backgroundColor: "rgba(194, 72, 72, 0.15);",
      color: "var(--layout-text-color)",
    },
    ".cm-delete": {
      backgroundColor: "rgba(194, 72, 72, 0.15);",
      color: "var(--layout-text-color)",
    },
    ".cm-insert": {
      backgroundColor: "rgba(47, 191, 125, 0.15);",
      color: "var(--layout-text-color)",
    },
    ".cm-tooltip": {
      backgroundColor: "var(--layout-background-color)",
      color: "var(--layout-text-color)",
      border: "1px solid var(--layout-border-color)",
    },
    ".cm-tooltip-autocomplete": {
      "& > ul > li": {
        backgroundColor: "var(--layout-background-color)",
        color: "var(--layout-text-color)",
      },
      "& > ul > li[aria-selected]": {
        backgroundColor: "var(--scaffold-accent-background-color)",
        color: "var(--layout-text-color)",
      }
    },
    ".cm-tooltip-autocomplete .cm-completionMatchedText": {
      textDecoration: "none !important",
      backgroundColor: "var(--layout-background-color)",
      color: "#45a1e5",
    },
    ".cm-ProcessedLine": {
      textDecoration: "underline",
    },
  }, {dark: (window as any).isDarkTheme});

  constructor(private codeEditorService: CodeEditorService) {}

  ngOnDestroy(): void {
    this.destroy$.next("");
  }

  ngOnChanges(changes) {
    if (!this.editorView) return;
    // two way data binding essentially here since we cant apply a control directly onto the editor
    if (
      changes?.control?.currentValue &&
      changes?.control?.currentValue?.value !==
        changes?.control?.previousValue?.value
    ) {
      this.updateEditorContent();
    }

    // update for style changes like errors or warnings to be inserted
    if (changes?.diagnostics) this.updateDiagnostics();
    // if (changes?.processedContent) this.setProcessedContent(); //check out later, not enough time to sort out, meant for marking previuosly ran content, still need to sort out resetting it
  }

  ngAfterViewInit(): void {
    const highlightStyle = HighlightStyle.define([
      { tag: tags.keyword, color: "#45a1e5;" },
    ]);
    const highlightExtension = syntaxHighlighting(highlightStyle);
    const customCompletions = (context: CompletionContext) => {
      let word = context.matchBefore(/[\w.]+/);
      if (word?.from == word?.to && !context.explicit) return null;

      return {
        from: word?.from,
        options: this.autocomplete || [],
      };
    };

    const basicSetup: Extension = (() => [
      lineNumbers(),
      highlightActiveLineGutter(),
      highlightSpecialChars(),
      history(),
      drawSelection(),
      dropCursor(),
      EditorState.allowMultipleSelections.of(true),
      indentOnInput(),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      bracketMatching(),
      closeBrackets(),
      autocompletion({ override: [customCompletions] }),
      rectangularSelection(),
      crosshairCursor(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      sql(), // avoid if making for other langs...
      keymap.of([
        indentWithTab,
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...searchKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...completionKeymap,
        ...lintKeymap,
      ]),
    ])();

    let editorElement = this.editor.nativeElement;
    let extensions = [
      basicSetup,
      this.theme,
      highlightExtension,
      EditorState.readOnly.of(this.readOnly),
    ];

    if (!this.readOnly) {
      extensions.push(
        EditorView.updateListener.of((v: ViewUpdate) => {
          const selectedText = v.state.sliceDoc(
            v.state.selection.main.from,
            v.state.selection.main.to
          );

          if (this.currentlyHighlightedText !== selectedText) {
            this.currentlyHighlightedText = selectedText;
            this.highlightedText.emit({
              text: this.currentlyHighlightedText,
              from: v.state.selection.main.from,
              to: v.state.selection.main.to,
            });
          }
          if (v.docChanged) {
            // this.clearDiagnostics();
          }
          if (this?.control?.setValue) {
            this.control.setValue(v.state.doc.toString());
          }
        })
      );
    }

    let state!: EditorState;

    try {
      state = EditorState.create({
        doc: this?.control?.value || "",
        extensions,
      });
    } catch (e) {
      console.log("e", e);
    }

    this.editorView = new EditorView({
      state,
      parent: editorElement,
    });

    if (this.diagnostics?.length) {
      this.updateDiagnostics();
    }

    this.editorView.dispatch({
      effects: StateEffect.appendConfig.of(
        EditorView.decorations.of(Decoration.set(this.decorations))
      ),
    });
    this.codeEditorService.addContent$
      .pipe(takeUntil(this.destroy$))
      .subscribe((content) => {
        const { state } = this.editorView;
        let { from, to } = state.selection.main;

        // avoid placing if yet set over the first content if available
        if (from === 0 && to === 0) {
          const initialConent = state?.doc?.text?.[0]?.length;
          if (initialConent) {
            from += initialConent + 1;
            to += initialConent + 1;
          }
        }

        const currentChar = state.doc.sliceString(from, to);
        if (currentChar.trim()) {
          this.editorView.dispatch({
            changes: {
              from,
              to,
              insert: " " + currentChar,
            },
          });
          from += 1;
          to += 1;
        }

        this.editorView.dispatch({
          changes: {
            from,
            to,
            insert: content,
          },
        });
      });
  }

  // Function to apply a class to a range
  applyClassToRange(from: number, to: number, className: string) {
    const doc = this.editorView.state.doc;
    const decorations = [];
    for (let i = from; i <= to; i++) {
      const line = doc.line(i);
      decorations.push(
        Decoration.line({
          class: "cm-ProcessedLine",
        }).range(line.from)
      );
    }

    this.editorView.dispatch({
      effects: StateEffect.appendConfig.of(
        EditorView.decorations.of(Decoration.set(decorations))
      ),
    });
  }

  setProcessedContent() {
    if (!this.processedContent?.text) return;

    const doc = this.editorView.state.doc;
    const from = doc.lineAt(this.processedContent.from).number;
    const to = doc.lineAt(this.processedContent.to).number;

    // Create decoration for highlighted lines
    const decorations = [];
    for (let i = from; i <= to; i++) {
      const line = doc.line(i);
      decorations.push(
        Decoration.line({
          class: "cm-ProcessedLine",
        }).range(line.from)
      );
    }

    this.editorView.dispatch({
      effects: StateEffect.appendConfig.of(
        EditorView.decorations.of(Decoration.set(decorations))
      ),
    });

  }

  // resets the editor content with control value
  updateEditorContent() {
    if (!this?.control) return;
    this.editorView.dispatch({
      changes: {
        from: 0,
        to: this.editorView.state.doc.length,
        insert: this.control.value,
      },
    });
  }

  clearDiagnostics() {
    this.editorView.dispatch(setDiagnostics(this.editorView.state, []));
  }

  updateDiagnostics() {
    const diagnostics: Diagnostic[] = this.getDiagnostics(
      this.editorView.state.doc.toString(),
      this.diagnostics
    );

    this.editorView.dispatch(
      setDiagnostics(this.editorView.state, diagnostics)
    );
  }

  getDiagnostics(doc: string, diagnosticsList = []): Diagnostic[] {
    const diagnostics: Diagnostic[] = [];
    const seen = new Set();
    // either work with the from/to provided or find it from the content provided instead for the error found
    // should also provide the from and if highlighted to also help determine where the error
    diagnosticsList.forEach(
      ({ content, severity, message, source, from, to }) => {
        if (
          typeof from === "number" &&
          typeof to === "number" &&
          from >= 0 &&
          to >= 1
        ) {
          diagnostics.push({
            from,
            to,
            severity,
            message: `${severity}: ${message}`,
          });
        } else {
          let fromContent = doc.indexOf(content);
          while (fromContent !== -1) {
            const to = fromContent + content.length;
            const key = `${fromContent}-${to}-${severity}-${message}-${source}`;
            if (!seen.has(key)) {
              diagnostics.push({
                from: fromContent,
                to,
                severity,
                message: `${severity}: ${message}`,
              });
              seen.add(key);
            }
            fromContent = doc.indexOf(content, to);
          }
        }
      }
    );

    return diagnostics;
  }
}
